import { useCallback, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, CircularProgress, Fade, IconButton, Stack, Tab, Tabs, Tooltip } from '@mui/material';

import { useCreateProposal } from 'api/projectVersioning/hooks/useCreateProposal';
import { useDiscardProposal } from 'api/projectVersioning/hooks/useDiscardProposal';
import type { Proposal } from 'api/projectVersioning/types';
import { useIsImportInProgressForProject } from 'state/imports/importsInProgress';

import { useAllProposalsData } from '../../hooks/useAllProposalsData';
import { CreateProposalDialog } from '../dialogs/CreateProposalDialog';
import { DiscardProposalDialog } from '../dialogs/DiscardProposalDialog';
import { ActiveProposalsList } from './ActiveProposalsList';
import { HistoryProposalsList } from './HistoryProposalsList';

type ProposalsTab = 'active' | 'history';

type AllProposalsContentProps = {
  navigateToProposal: (targetProposalId: string) => void;
  closeDialog: () => void;
  projectId: string;
};

export const AllProposalsContent: React.FC<AllProposalsContentProps> = ({
  navigateToProposal,
  closeDialog,
  projectId
}) => {
  const [activeTab, setActiveTab] = useState<ProposalsTab>('active');
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [proposalForMenu, setProposalForMenu] = useState<Proposal | null>(null);
  const [proposalToDiscard, setProposalToDiscard] = useState<Proposal | null>(null);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [isCreateProposalOpen, setIsCreateProposalOpen] = useState(false);
  const {
    activeProposals,
    historyProposals,
    isActiveProposalsLoading,
    isHistoryProposalsLoading,
    isActiveProposalsError,
    isHistoryProposalsError
  } = useAllProposalsData();
  const { mutateAsync: createProposalAsync, isPending: isCreateProposalPending } = useCreateProposal();
  const { mutateAsync: discardProposalAsync, isPending: isDiscardPending } = useDiscardProposal();
  const { isImportInProgress, isImportStatusLoading } = useIsImportInProgressForProject(projectId);
  const loading = isDiscardPending || isImportStatusLoading;
  const openMenu = useCallback((event: React.MouseEvent<HTMLElement>, proposal: Proposal) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setProposalForMenu(proposal);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuAnchorEl(null);
    setProposalForMenu(null);
  }, []);

  const openProposal = useCallback(
    (targetProposalId: string) => {
      closeMenu();
      closeDialog();
      navigateToProposal(targetProposalId);
    },
    [closeDialog, closeMenu, navigateToProposal]
  );

  const openDiscardDialog = useCallback(() => {
    if (proposalForMenu) setProposalToDiscard(proposalForMenu);
    closeMenu();
    setIsDiscardDialogOpen(true);
  }, [closeMenu, proposalForMenu]);

  const closeDiscardDialog = useCallback(() => {
    setIsDiscardDialogOpen(false);
    setProposalToDiscard(null);
  }, []);

  const handleDiscardProposal = useCallback(async () => {
    if (!proposalToDiscard) return;
    await discardProposalAsync({ projectId: proposalToDiscard.projectId, proposalId: proposalToDiscard.id });
    setIsDiscardDialogOpen(false);
    setProposalToDiscard(null);
  }, [discardProposalAsync, proposalToDiscard]);

  const handleCreateProposal = useCallback(
    async (name: string) => {
      if (!projectId) return;
      // A failure is already reported by useCreateProposal's onError; swallow it so it isn't unhandled.
      const result = await createProposalAsync({ projectId, payload: { name } }).catch(() => undefined);
      const newProposalId = result?.id;
      if (!newProposalId) return;

      setIsCreateProposalOpen(false);
      closeDialog();
      navigateToProposal(newProposalId);
    },
    [closeDialog, createProposalAsync, navigateToProposal, projectId]
  );

  const tabConfig = {
    active: {
      proposals: activeProposals,
      isLoading: isActiveProposalsLoading,
      isError: isActiveProposalsError,
      id: 'active-proposals-list',
      ariaLabel: 'Active proposals list'
    },
    history: {
      proposals: historyProposals,
      isLoading: isHistoryProposalsLoading,
      isError: isHistoryProposalsError,
      id: 'history-proposals-list',
      ariaLabel: 'History proposals list'
    }
  } satisfies Record<
    ProposalsTab,
    {
      proposals: Proposal[];
      isLoading: boolean;
      isError: boolean;
      id: string;
      ariaLabel: string;
    }
  >;

  const currentTabConfig = tabConfig[activeTab];

  const renderProposalsList = () => {
    const content =
      activeTab === 'active' ? (
        <ActiveProposalsList
          proposals={activeProposals}
          menuAnchorEl={menuAnchorEl}
          proposalForMenu={proposalForMenu}
          onOpenMenu={openMenu}
          onCloseMenu={closeMenu}
          navigateToProposal={openProposal}
          onDiscardProposal={openDiscardDialog}
          disabled={isImportInProgress}
          loading={isImportStatusLoading}
        />
      ) : (
        <HistoryProposalsList proposals={historyProposals} />
      );

    return (
      <Fade in timeout={400} mountOnEnter unmountOnExit>
        <Box role='tabpanel' id={currentTabConfig.id} aria-labelledby={currentTabConfig.ariaLabel}>
          {content}
        </Box>
      </Fade>
    );
  };

  let content;
  if (currentTabConfig.isLoading) {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress />
      </Box>
    );
  } else if (currentTabConfig.isError) {
    content = (
      <Alert severity='error' sx={{ my: 2 }} data-testid='all-proposals-error'>
        {`Could not load ${activeTab} proposals.`}
      </Alert>
    );
  } else {
    content = renderProposalsList();
  }

  return (
    <Stack
      sx={{
        maxHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Tabs
          value={activeTab}
          variant='fullWidth'
          onChange={(_, newValue: ProposalsTab) => setActiveTab(newValue)}
          aria-label='All proposals tabs'
        >
          <Tab label='Active' value='active' data-testid='all-proposals-tab-active' />
          <Tab label='History' value='history' data-testid='all-proposals-tab-history' />
        </Tabs>

        <Tooltip title='Create new proposal'>
          <IconButton
            onClick={() => setIsCreateProposalOpen(true)}
            aria-label='Create new proposal'
            size='small'
            data-testid='all-proposals-create-button'
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Stack sx={{ overflow: 'auto' }}>{content}</Stack>

      <DiscardProposalDialog
        open={isDiscardDialogOpen && !!proposalToDiscard}
        proposalName={proposalToDiscard?.name ?? ''}
        onConfirm={handleDiscardProposal}
        onClose={closeDiscardDialog}
        loading={loading}
        disabled={isImportInProgress}
      />

      {isCreateProposalOpen && (
        <CreateProposalDialog
          onConfirm={handleCreateProposal}
          onClose={() => setIsCreateProposalOpen(false)}
          isLoading={isCreateProposalPending}
        />
      )}
    </Stack>
  );
};
